// ----------------------------------------------------------------
// <copyright company='Microsoft Corporation'>
//     Copyright (c) Microsoft Corporation.  All rights reserved.
// </copyright>
// ----------------------------------------------------------------

import { Icon } from 'office-ui-fabric-react/lib/Icon';
import * as React from 'react';
import { useTranslation } from 'react-i18next';

import Collapsible from './../../components/Collapsible';
import LineItem from './../../components/LineItem';
import {
    IAddressInfo,
    ILineItem,
    IServiceRequest,
} from './../../businessLogic/sagas/serviceRequest';

import Styles from './serviceRequest.module.scss';

interface PropsType {
    agentTier: number;
    claimId: string;
    data: IServiceRequest;
    isOpen?: boolean;
    reasonForNoRepair: string;
    reasonForNoRepairDesc: string;
    claimOrderId: string;
    serviceLocationDetails: IAddressInfo;
    countryCode: string;
}

function RenderCheck(status: string) {
    return status.toLocaleUpperCase() === 'OPEN' && <Icon className={Styles.statusIcon} iconName="CheckMark" />;
}

export default function ServiceRequest(props: PropsType) {
    const { t } = useTranslation();
    return (
        <Collapsible
            colorOnOpen={'doveGray'}
            colorOnClose={'doveGray'}
            chevronColor={'alabaster'}
            header={ServiceRequestHeader(props.data)}
            className={Styles.serviceRequest}
            isOpen={props.isOpen}
            tableName={t('service_request_number')}
        >
            <>
                <div className={Styles.lineItems}>
                    {props.data.lineItems.map(
                        (lineItem: ILineItem, key: number) => <LineItem
                            claimId={props.claimId}
                            data={lineItem}
                            isOpen={props.data.lineItems.length === 1}
                            key={key}
                            serviceRequestNumber={props.data.serviceRequestNumber}
                            status={props.data.status}
                            claimOrderId={props.claimOrderId}
                            zipCode={props.serviceLocationDetails?.postalCode}
                            countryCode={props.countryCode}
                        />
                    )}
                </div>               
            </>
        </Collapsible>
    );
}

function ServiceRequestHeader(data: IServiceRequest) {
    const { t } = useTranslation();
    
    return (
        <div className={Styles.header}>
            <div className={Styles.order}>
                <div className={Styles.orderNumberLabel}>{t('service_request_number')}:</div>
                <div className={Styles.orderNumber}>{data.serviceRequestNumber}</div>
            </div>
            <div className={Styles.status}>
                <div className={Styles.statusLabel}>{t('status')}:</div>
                {RenderCheck(data.status)}
                <div className={Styles.statusState}>{t(data.status)}</div>
            </div>
        </div>
    );
}